// ============================================
// Dispatcher — Children View (Read-Only)
// Monitoring transported children
// Premium design matching AdminDashboard quality
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import DispatcherLayout from '../../components/dispatcher/DispatcherLayout';
import dispatcherService from '../../services/dispatcherService';
import { decodeHtml } from '../../utils/decodeHtml';

const avatarGradients = [
    'from-pink-400 to-rose-500',
    'from-amber-400 to-orange-500',
    'from-emerald-400 to-teal-500',
    'from-sky-400 to-blue-500',
    'from-violet-400 to-purple-500',
];

const DispatcherChildren = () => {
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const fetchChildren = useCallback(async () => {
        try {
            setLoading(true);
            const res = await dispatcherService.getChildren();
            if (res.data.success) setChildren(res.data.data.children || []);
        } catch (err) {
            console.error('Error fetching children:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchChildren(); }, [fetchChildren]);

    const filtered = children.filter(c =>
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.schoolId?.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.parentName?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <DispatcherLayout title="Children Transported" subtitle="Safe student transport monitoring">
            {/* Top Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 relative z-10">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-[1rem] bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center text-white shadow-lg shadow-rose-500/20">
                        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
                            <path d="M12 12a5 5 0 100-10 5 5 0 000 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M20 21v-2a7 7 0 00-16 0v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div>
                        <p className="text-xl font-black text-slate-900 dark:text-white tracking-tight">{filtered.length} Children</p>
                        <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">Transport list • Read-only access</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {/* Search */}
                    <div className="flex items-center gap-2 px-5 py-3 rounded-[1rem] bg-white/60 dark:bg-slate-900/40 backdrop-blur-md border border-white/60 dark:border-white/10 shadow-sm focus-within:border-emerald-500/50 dark:focus-within:border-emerald-500/50 focus-within:ring-4 focus-within:ring-emerald-500/10 transition-all w-64">
                        <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text" placeholder="Search children..." value={search} onChange={e => setSearch(e.target.value)}
                            className="bg-transparent text-[11px] font-bold tracking-wide uppercase text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none flex-1"
                        />
                    </div>
                    <button onClick={fetchChildren} className="px-5 py-3 rounded-[1rem] text-[11px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 bg-emerald-50/80 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 border border-emerald-200/50 dark:border-emerald-500/20 transition-all duration-300 hover:scale-105 shadow-sm flex items-center gap-2">
                        ↻ Refresh
                    </button>
                </div>
            </div>

            {/* Children Table */}
            <div className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl relative z-10">
                <div className="w-full overflow-x-auto max-h-[70vh] custom-scrollbar">
                    <table className="w-full">
                        <thead className="sticky top-0 z-20">
                            <tr className="bg-slate-50/80 dark:bg-slate-800/80 backdrop-blur-md">
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest whitespace-nowrap">Student</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest whitespace-nowrap">School</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest whitespace-nowrap">Parent/Guardian</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest whitespace-nowrap">Contact</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest whitespace-nowrap">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="5" className="px-6 py-6"><div className="h-5 bg-slate-200/50 dark:bg-slate-800 rounded-lg w-full" /></td>
                                    </tr>
                                ))
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center">
                                        <div className="w-16 h-16 mx-auto rounded-[1.5rem] bg-emerald-500/10 flex items-center justify-center mb-6 shadow-inner text-emerald-500/50 dark:text-emerald-400/50">
                                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                        <p className="text-sm font-bold text-slate-500 dark:text-slate-400">No children found</p>
                                        <p className="text-[11px] font-bold tracking-widest uppercase text-slate-400/70 mt-1">Try adjusting your search criteria</p>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((c, i) => (
                                    <tr key={c._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors duration-300">
                                        <td className="px-6 py-5 whitespace-nowrap">
                                            <div className="flex items-center gap-4">
                                                <div className={`w-12 h-12 rounded-[1rem] bg-gradient-to-br ${avatarGradients[i % 5]} flex items-center justify-center text-white text-sm font-black shadow-lg shadow-${avatarGradients[i % 5].split('-')[1]}-500/30 hover:scale-110 transition-transform`}>
                                                    {c.name?.split(' ').map(n => n[0]).join('')}
                                                </div>
                                                <span className="text-sm font-black text-slate-900 dark:text-white">{c.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-black text-slate-700 dark:text-slate-200">{decodeHtml(c.schoolId?.name) || 'Private'}</span>
                                                <span className="text-[10px] font-bold tracking-widest uppercase text-emerald-600 dark:text-emerald-400 mt-0.5">Campus A</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-sm font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap">{c.parentName || '—'}</td>
                                        <td className="px-6 py-5 text-[11px] font-bold tracking-wider uppercase text-slate-500 dark:text-slate-400 whitespace-nowrap">{c.phone || '—'}</td>
                                        <td className="px-6 py-5 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest shadow-sm bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/20">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-pulse" />
                                                Active
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </DispatcherLayout>
    );
};

export default DispatcherChildren;
